import { FontFamily } from '@euler/components/typography/fonts';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import React, {
  Component,
  createContext,
  memo,
  ReactNode,
  useContext,
} from 'react';
import { ScrollView, Text, useWindowDimensions, View } from 'react-native';
import {
  CHART_COLORS,
  ChartLegend,
  showChartLegend,
  useChartStyles,
} from './chartTheme';
import { HorizontalBarChartWidget } from './HorizontalBarChartWidget';
import {
  CartesianChartPayload,
  ChartPayload,
  ChartSeries,
  ChartSeriesRow,
  PieChartPayload,
  PyramidChartPayload,
  RadarChartPayload,
  RadarRow,
} from './payloads';
import { useWidgetStyles } from './styles';

/**
 * The width available to a chart, set by a width-bounded host (e.g. a chat
 * message bubble). When null the chart falls back to the full window — the
 * right behavior in the full-width AI panel.
 */
export const ChartWidthContext = createContext<number | null>(null);

// Chart drawing height + the x-axis label band. The horizontal ScrollView
// is bounded to this so a nested scroll view can't balloon vertically on
// native (it wraps content fine on web, but grabs unbounded height on RN).
const CHART_AREA_HEIGHT = 160;
const CHART_SCROLL_HEIGHT = CHART_AREA_HEIGHT + 36;
// The narrowest y-axis label band; `yAxisWidth` widens it for long labels.
const FIT_Y_AXIS_WIDTH = 40;
// Per character of a y label at the 9-px axis font, plus the band's gap.
const Y_LABEL_CHAR_WIDTH = 6;
const Y_LABEL_GAP = 10;

/**
 * A y-axis band wide enough for the longest label the values produce:
 * 8-digit amounts would otherwise be cut off at the plot's edge.
 */
function yAxisWidth(values: Array<number | undefined>): number {
  let max = 0;
  let negative = false;
  for (const value of values) {
    if (value == null || !Number.isFinite(value)) continue;
    max = Math.max(max, Math.abs(value));
    negative ||= value < 0;
  }
  // One more for the axis top, which rounds up past the largest value.
  const chars = String(Math.ceil(max)).length + 1 + (negative ? 1 : 0);
  return Math.max(FIT_Y_AXIS_WIDTH, chars * Y_LABEL_CHAR_WIDTH + Y_LABEL_GAP);
}

/** A cell as a plotted number; a missing one stays missing. */
function plotValue(value: string | number | undefined): number | undefined {
  if (value === undefined || value === '') return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

/**
 * Props that make a line/bar chart fit a bounded width with no scroll. We
 * pass BOTH `parentWidth` (drives `adjustToWidth`'s auto bar/spacing) and an
 * explicit `width` (the plot canvas) — native needs a concrete width; web
 * tolerates undefined. Total = yAxis band + plot ≈ the bounded width.
 */
const fitChartProps = (
  boundedWidth: number,
  yAxisLabelWidth: number,
  endSpacing = 0,
) => ({
  adjustToWidth: true as const,
  disableScroll: true as const,
  parentWidth: boundedWidth,
  // Reserve the y-axis band + (line charts) a right margin, so the plot +
  // trailing space fits the bounded width without the last point flush
  // against the edge.
  width: Math.max(boundedWidth - yAxisLabelWidth - endSpacing, 80),
  yAxisLabelWidth,
  ...(endSpacing ? { endSpacing } : {}),
});

/**
 * Strip the year prefix from ISO-date x-axis labels (`2026-06-14` →
 * `06-14`). Anything else passes through unchanged so short custom
 * labels (weekday, category name, plate number) aren't mangled.
 */
function formatAxisLabel(val: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val.slice(5);
  return val;
}

function useChartWidth() {
  const ctxW = useContext(ChartWidthContext);
  const { rpx } = useResponsiveContext();
  const { width } = useWindowDimensions();
  // Inside a bounded host (chat bubble) this is the chart-container INNER
  // width, passed as `parentWidth` with `adjustToWidth` — gifted-charts then
  // fits the y-axis + plot inside it. Otherwise fall back to the full window
  // (AI panel, scrollable).
  return ctxW != null ? Math.max(ctxW - rpx(24), rpx(160)) : width - 100;
}

/**
 * True when rendered in a width-bounded host (a chat bubble): the chart then
 * fits the width with no horizontal scroll. Unbounded (the full-width AI
 * panel) keeps the scroll so wide series aren't cramped.
 */
function useChartFit() {
  return useContext(ChartWidthContext) != null;
}

/**
 * Hosts a chart body: fit-to-width in a plain View (chat) vs a bounded
 * horizontal ScrollView (panel). A horizontal ScrollView needs an explicit
 * height on native, hence CHART_SCROLL_HEIGHT.
 */
const ChartScrollHost = ({
  fit,
  children,
}: {
  fit: boolean;
  children: ReactNode;
}) =>
  fit ? (
    <View>{children}</View>
  ) : (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ height: CHART_SCROLL_HEIGHT }}
    >
      {children}
    </ScrollView>
  );

class ChartErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <View
          style={{
            backgroundColor: '#F8FAFC',
            borderRadius: 10,
            padding: 12,
            marginVertical: 6,
          }}
        >
          <Text
            style={{
              fontSize: 12,
              fontFamily: FontFamily.NotoSans.Regular,
              color: '#64748B',
            }}
          >
            图表渲染失败
          </Text>
        </View>
      );
    }
    return this.props.children;
  }
}

export const ChartWidget = memo(({ data }: { data: ChartPayload }) => {
  const ws = useWidgetStyles();
  if (!data?.type || !data?.data) return null;

  // Switching on `data.type` (not a copied local) lets TS narrow the payload
  // union to the concrete member each sub-widget expects.
  let chart: ReactNode = null;
  switch (data.type) {
    case 'line':
    case 'area':
      chart = <LineChartWidget data={data} isArea={data.type === 'area'} />;
      break;
    case 'bar':
      chart = <BarChartWidget data={data} />;
      break;
    case 'horizontal_bar':
      chart = <HorizontalBarChartWidget data={data} />;
      break;
    case 'stacked_bar':
      chart = <StackedBarChartWidget data={data} />;
      break;
    case 'pie':
    case 'donut':
      chart = <PieChartWidget data={data} isDonut={data.type === 'donut'} />;
      break;
    case 'radar':
      chart = <RadarChartWidget data={data} />;
      break;
    case 'pyramid':
      chart = <PyramidChartWidget data={data} />;
      break;
    default:
      return (
        <View style={ws.card}>
          <Text style={ws.meta}>
            {'不支持的图表类型: ' + (data as { type?: string }).type}
          </Text>
        </View>
      );
  }

  return <ChartErrorBoundary>{chart}</ChartErrorBoundary>;
});

function LazyLineChart(props: any) {
  const Comp = require('@euler/lib/charts').LineChart;
  return <Comp {...props} />;
}

function LazyBarChart(props: any) {
  const Comp = require('@euler/lib/charts').BarChart;
  return <Comp {...props} />;
}

function LazyPieChart(props: any) {
  const Comp = require('@euler/lib/charts').PieChart;
  return <Comp {...props} />;
}

function LazyRadarChart(props: any) {
  const Comp = require('@euler/lib/charts').RadarChart;
  return <Comp {...props} />;
}

function LazyPopulationPyramid(props: any) {
  const Comp = require('@euler/lib/charts').PopulationPyramid;
  return <Comp {...props} />;
}

const LineChartWidget = memo(
  ({ data, isArea }: { data: CartesianChartPayload; isArea: boolean }) => {
    const chartWidth = useChartWidth();
    const fit = useChartFit();
    const s = useChartStyles();
    const series = data.series ?? [];
    const xKey = data.xKey ?? 'date';
    const items = data.data ?? [];

    if (items.length === 0) return null;

    const yWidth = yAxisWidth(
      items.flatMap(item => series.map(sr => plotValue(item[sr.key]))),
    );
    const fitProps = fitChartProps(chartWidth, yWidth, 16);
    // Fit mode packs every point into the bubble, so thin the labels to
    // what their width allows; scroll mode spaces points out instead.
    const step = fit
      ? Math.max(
          1,
          Math.ceil((items.length * LINE_LABEL_WIDTH) / fitProps.width),
        )
      : items.length > 15
      ? Math.ceil(items.length / 8)
      : 1;
    const firstKey = series[0]?.key;

    // A missing value is a gap in the line, never a drop to 0.
    const lineData = items.map((item, idx) => ({
      value: firstKey ? plotValue(item[firstKey]) : undefined,
      label: idx % step === 0 ? formatAxisLabel(String(item[xKey] ?? '')) : '',
    }));

    // gifted-charts draws each label in a box one point-spacing wide, which
    // truncates a date in fit mode; the component lets it overhang its box,
    // centered on the point.
    const labelComponent = (label: string) => () => (
      <View
        style={{
          width: LINE_LABEL_WIDTH,
          alignSelf: 'center',
          alignItems: 'center',
        }}
      >
        <Text numberOfLines={1} style={s.chartAxisText}>
          {label}
        </Text>
      </View>
    );
    // A curve bends through the hidden zero at the edge of a gap, so a
    // chart with gaps is drawn straight.
    const gaps = items.some(item =>
      series.some(sr => plotValue(item[sr.key]) === undefined),
    );
    const dataSets = series.map((sr, si) => {
      const values = items.map(item => plotValue(item[sr.key]));
      const color = sr.color ?? CHART_COLORS[si % CHART_COLORS.length];
      return {
        data: values.map((value, idx) => ({
          value,
          ...(si === 0 && lineData[idx].label
            ? { labelComponent: labelComponent(lineData[idx].label) }
            : {}),
        })),
        color,
        thickness: 2,
        // A gap would still fill down to the baseline, so a series with
        // gaps is drawn as its line only.
        ...(isArea
          ? {
              startFillColor: gaps ? '#ffffff00' : color + '40',
              endFillColor: '#ffffff00',
            }
          : {}),
      };
    });

    return (
      <View style={s.chartContainer}>
        {data.title ? <Text style={s.chartTitle}>{data.title}</Text> : null}
        <ChartScrollHost fit={fit}>
          <LazyLineChart
            data={lineData}
            dataSet={dataSets}
            {...(fit
              ? fitProps
              : {
                  width: Math.max(chartWidth, items.length * 28),
                  spacing: items.length > 20 ? 22 : 36,
                  yAxisLabelWidth: yWidth,
                })}
            height={CHART_AREA_HEIGHT}
            color="transparent"
            thickness={0}
            areaChart={isArea}
            interpolateMissingValues={false}
            noOfSections={4}
            yAxisTextStyle={s.chartAxisText}
            xAxisLabelTextStyle={s.chartAxisText}
            hideDataPoints={items.length > 20}
            curved={!gaps}
            isAnimated
          />
        </ChartScrollHost>
        {showChartLegend(data) ? <ChartLegend series={series} /> : null}
      </View>
    );
  },
);

const BarChartWidget = memo(({ data }: { data: CartesianChartPayload }) => {
  const chartWidth = useChartWidth();
  const fit = useChartFit();
  const s = useChartStyles();
  const series = data.series ?? [];
  const xKey = data.xKey ?? 'name';
  const items = data.data ?? [];

  if (items.length === 0 || series.length === 0) return null;

  const step = items.length > 15 ? Math.ceil(items.length / 8) : 1;
  const label = (item: ChartSeriesRow, idx: number) =>
    idx % step === 0 ? formatAxisLabel(String(item[xKey] ?? '')) : '';

  const singleSeries = series.length === 1;
  const [firstSeries] = series;
  const yWidth = yAxisWidth(
    items.flatMap(item => series.map(sr => plotValue(item[sr.key]))),
  );
  const fitProps = fitChartProps(chartWidth, yWidth);
  const grouped = groupedBarSizes(
    items.length,
    series.length,
    fit ? fitProps.width : null,
  );

  return (
    <View style={s.chartContainer}>
      {data.title ? <Text style={s.chartTitle}>{data.title}</Text> : null}
      <ChartScrollHost fit={fit}>
        <LazyBarChart
          {...(fit
            ? fitProps
            : {
                width: Math.max(
                  chartWidth,
                  singleSeries ? items.length * 50 : grouped.width,
                ),
                barWidth: 28,
                spacing: 16,
                yAxisLabelWidth: yWidth,
              })}
          {...(singleSeries
            ? {
                data: items.map((item, idx) => ({
                  value: Number(item[firstSeries.key] ?? 0),
                  label: label(item, idx),
                  frontColor:
                    firstSeries.color ?? CHART_COLORS[0 % CHART_COLORS.length],
                })),
              }
            : {
                // Grouped bars: one bar per series side by side, a tight gap
                // inside a group and the group spacing after its last bar.
                // The group's label sits on its first bar, spanning the
                // group. Colors come from the SERIES, so the legend matches.
                barWidth: grouped.barWidth,
                spacing: grouped.groupSpacing,
                initialSpacing: grouped.groupSpacing / 2,
                data: items.flatMap((item, idx) =>
                  series.map((sr, si) => ({
                    value: Number(item[sr.key] ?? 0),
                    frontColor:
                      sr.color ?? CHART_COLORS[si % CHART_COLORS.length],
                    spacing:
                      si < series.length - 1
                        ? GROUPED_BAR_GAP
                        : grouped.groupSpacing,
                    ...(si === 0
                      ? { label: label(item, idx), labelWidth: grouped.span }
                      : {}),
                  })),
                ),
              })}
          height={CHART_AREA_HEIGHT}
          noOfSections={4}
          yAxisTextStyle={s.chartAxisText}
          xAxisLabelTextStyle={s.chartAxisText}
          isAnimated
        />
      </ChartScrollHost>
      {showChartLegend(data) ? <ChartLegend series={series} /> : null}
    </View>
  );
});

const GROUPED_BAR_GAP = 2;
// The width a line chart's x label may take, centered on its point.
const LINE_LABEL_WIDTH = 44;

/**
 * Bar and spacing widths for grouped bars. Fit mode splits the plot width
 * into one section per group, 60% bars and 40% spacing — the proportions
 * gifted-charts' own `adjustToWidth` uses for single bars. Scroll mode
 * uses fixed bars and grows the canvas instead.
 */
function groupedBarSizes(
  groups: number,
  perGroup: number,
  plotWidth: number | null,
) {
  const gaps = GROUPED_BAR_GAP * (perGroup - 1);
  if (plotWidth == null) {
    const barWidth = 14;
    const groupSpacing = 16;
    const span = barWidth * perGroup + gaps;
    return { barWidth, groupSpacing, span, width: groups * (span + 16) };
  }
  const section = plotWidth / (groups + 0.5);
  const barWidth = Math.max((section * 0.6 - gaps) / perGroup, 2);
  const span = barWidth * perGroup + gaps;
  return { barWidth, groupSpacing: section * 0.4, span, width: plotWidth };
}

const StackedBarChartWidget = memo(
  ({ data }: { data: CartesianChartPayload }) => {
    const chartWidth = useChartWidth();
    const fit = useChartFit();
    const s = useChartStyles();
    const series = data.series ?? [];
    const xKey = data.xKey ?? 'date';
    const items = data.data ?? [];

    if (items.length === 0 || series.length === 0) return null;

    const step = items.length > 15 ? Math.ceil(items.length / 8) : 1;

    const yWidth = yAxisWidth(
      items.map(item =>
        series.reduce((sum, sr) => sum + (plotValue(item[sr.key]) ?? 0), 0),
      ),
    );
    const stackData = items.map((item, idx) => ({
      stacks: series.map((sr, si) => ({
        value: Number(item[sr.key] ?? 0),
        color: sr.color ?? CHART_COLORS[si % CHART_COLORS.length],
      })),
      label: idx % step === 0 ? formatAxisLabel(String(item[xKey] ?? '')) : '',
    }));

    return (
      <View style={s.chartContainer}>
        {data.title ? <Text style={s.chartTitle}>{data.title}</Text> : null}
        <ChartScrollHost fit={fit}>
          <LazyBarChart
            stackData={stackData}
            {...(fit
              ? fitChartProps(chartWidth, yWidth)
              : {
                  width: Math.max(chartWidth, items.length * 32),
                  barWidth: 20,
                  spacing: 12,
                  yAxisLabelWidth: yWidth,
                })}
            height={CHART_AREA_HEIGHT}
            noOfSections={4}
            yAxisTextStyle={s.chartAxisText}
            xAxisLabelTextStyle={s.chartAxisText}
            isAnimated
          />
        </ChartScrollHost>
        {data.legend === false ? null : <ChartLegend series={series} />}
      </View>
    );
  },
);

const PieChartWidget = memo(
  ({ data, isDonut }: { data: PieChartPayload; isDonut: boolean }) => {
    const s = useChartStyles();
    const items = data.data ?? [];
    if (items.length === 0) return null;

    const pieData = items.map((item, i) => ({
      value: Number(item.value ?? 0),
      color: item.color ?? CHART_COLORS[i % CHART_COLORS.length],
      text: '',
    }));

    return (
      <View style={s.chartContainer}>
        {data.title ? <Text style={s.chartTitle}>{data.title}</Text> : null}
        <View style={{ alignItems: 'center' }}>
          <LazyPieChart
            data={pieData}
            donut={isDonut}
            radius={80}
            innerRadius={isDonut ? 50 : undefined}
            centerLabelComponent={
              isDonut && data.centerText
                ? () => (
                    <View style={{ alignItems: 'center' }}>
                      <Text style={s.donutCenterText}>{data.centerText}</Text>
                    </View>
                  )
                : undefined
            }
            isAnimated
          />
        </View>
        <View style={[s.chartLegend, { flexWrap: 'wrap' }]}>
          {items.map((item, i) => (
            <View key={i} style={s.chartLegendItem}>
              <View
                style={[
                  s.chartLegendDot,
                  {
                    backgroundColor:
                      item.color ?? CHART_COLORS[i % CHART_COLORS.length],
                  },
                ]}
              />
              <Text style={s.chartLegendText}>
                {item.name} ({Number(item.value ?? 0).toLocaleString('zh-CN')})
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  },
);

const RadarChartWidget = memo(({ data }: { data: RadarChartPayload }) => {
  const s = useChartStyles();
  const items = data.data ?? [];
  if (items.length === 0) return null;

  const rawSeries = data.series ?? [];
  const seriesEntries: ChartSeries[] = rawSeries.map(sr =>
    typeof sr === 'string' ? { name: sr, key: sr } : sr,
  );
  const seriesNames = seriesEntries.map(sr => sr.name ?? '');

  const hasValuesArray = Array.isArray(items[0]?.values);
  const hasSeriesKeys =
    !hasValuesArray &&
    seriesEntries.length > 0 &&
    seriesEntries[0].key in items[0];

  interface PolygonConfig {
    stroke: string;
    strokeWidth: number;
    fill: string;
  }
  const polygonConfigFor = (si: number): PolygonConfig => ({
    stroke: CHART_COLORS[si % CHART_COLORS.length],
    strokeWidth: 2.5,
    fill: CHART_COLORS[si % CHART_COLORS.length] + '40',
  });

  let labels: string[] = [];
  let radarData: number[] | undefined = undefined;
  let dataSet: number[][] | undefined = undefined;
  let polygonConfigArray: PolygonConfig[] | undefined = undefined;

  if (hasValuesArray) {
    // Multi-series wide rows: `{ label, values: [v0, v1, ...] }`.
    labels = data.labels ?? items.map(d => String(d.label ?? ''));
    const valuesOf = (d: RadarRow) => (d.values as number[] | undefined) ?? [];
    const count = valuesOf(items[0]).length;
    dataSet = Array.from({ length: count }, (_, si) =>
      items.map(d => Number(valuesOf(d)[si] ?? 0)),
    );
    polygonConfigArray = Array.from({ length: count }, (_, si) =>
      polygonConfigFor(si),
    );
  } else if (hasSeriesKeys) {
    // One field per series keyed on the row: `{ [xKey], [seriesKey]: n }`.
    const xKey = data.xKey ?? 'label';
    labels = items.map(d => String(d[xKey] ?? d.label ?? ''));
    dataSet = seriesEntries.map(sr => items.map(d => Number(d[sr.key] ?? 0)));
    polygonConfigArray = seriesEntries.map((_, si) => polygonConfigFor(si));
  } else {
    // Single-series long rows: `{ label, value }`.
    labels = data.labels ?? items.map(d => String(d.label ?? ''));
    radarData = items.map(d => Number(d.value ?? 0));
  }

  return (
    <View style={s.chartContainer}>
      {data.title ? <Text style={s.chartTitle}>{data.title}</Text> : null}
      <View style={{ alignItems: 'center' }}>
        <LazyRadarChart
          data={radarData}
          dataSet={dataSet}
          labels={labels}
          chartSize={160}
          chartContainerProps={{
            height: 260,
            width: 260,
            shiftX: 50,
            shiftY: 50,
          }}
          maxValue={100}
          noOfSections={5}
          gridSections={[
            { stroke: '#E2E8F0', fill: '#F8FAFC' },
            { stroke: '#E2E8F0', fill: '#F1F5F9' },
            { stroke: '#E2E8F0', fill: '#F8FAFC' },
            { stroke: '#E2E8F0', fill: '#F1F5F9' },
            { stroke: '#E2E8F0', fill: '#F8FAFC' },
          ]}
          asterLinesConfig={{
            stroke: '#CBD5E1',
            strokeWidth: 1,
          }}
          labelsPositionOffset={18}
          polygonConfig={
            radarData
              ? {
                  stroke: CHART_COLORS[0],
                  strokeWidth: 2.5,
                  fill: CHART_COLORS[0] + '40',
                }
              : undefined
          }
          polygonConfigArray={polygonConfigArray}
        />
      </View>
      {seriesNames.length > 1 && (
        <View style={s.chartLegend}>
          {seriesNames.map((name, i) => (
            <View key={i} style={s.chartLegendItem}>
              <View
                style={[
                  s.chartLegendDot,
                  {
                    backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                  },
                ]}
              />
              <Text style={s.chartLegendText}>{name}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
});

const PyramidChartWidget = memo(({ data }: { data: PyramidChartPayload }) => {
  const chartWidth = useChartWidth();
  const s = useChartStyles();
  const items = data.data ?? [];
  if (items.length === 0) return null;

  const pyramidData = items.map(d => ({
    left: Number(d.left ?? 0),
    right: Number(d.right ?? 0),
    leftBarColor: d.leftColor ?? CHART_COLORS[0],
    rightBarColor: d.rightColor ?? CHART_COLORS[1],
  }));

  const midLabels = items.map(d => d.label ?? '');

  return (
    <View style={s.chartContainer}>
      {data.title ? <Text style={s.chartTitle}>{data.title}</Text> : null}
      <LazyPopulationPyramid
        data={pyramidData}
        showMidAxis
        midAxisLabel={midLabels}
        leftBarLabel={data.leftLabel}
        rightBarLabel={data.rightLabel}
        width={chartWidth}
        isAnimated
      />
      {(data.leftLabel || data.rightLabel) && (
        <View style={s.chartLegend}>
          {data.leftLabel ? (
            <View style={s.chartLegendItem}>
              <View
                style={[s.chartLegendDot, { backgroundColor: CHART_COLORS[0] }]}
              />
              <Text style={s.chartLegendText}>{data.leftLabel}</Text>
            </View>
          ) : null}
          {data.rightLabel ? (
            <View style={s.chartLegendItem}>
              <View
                style={[s.chartLegendDot, { backgroundColor: CHART_COLORS[1] }]}
              />
              <Text style={s.chartLegendText}>{data.rightLabel}</Text>
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
});
